/**
 * The declaration window.
 *
 * Three things it shows and one it deliberately does not. It shows the round,
 * who is standing, and **declaration status** — declared / still thinking —
 * which is what the room needs while a window is open. It does not show a
 * turn-order queue or a current actor: everyone declares in parallel and
 * initiative is rolled per side *after* the window closes, so there is no
 * queue to draw. Drawing one would mean inventing a model the server does not
 * have.
 *
 * The initiative that *is* shown appears only once `declare:close` has
 * carried it, ascending, because low acts first in this ruleset.
 */

import { useEffect, useState } from 'react';

import type { CombatActionDescriptor, SceneFeature } from '@portal/shared';

import { initiativeOrder, waitingOn, zoneProse, type CombatView } from '../realtime/combat.js';
import { verbLabel } from '../verbs/cost.js';
import type { CombatDeclaration } from '../realtime/socket.js';

/**
 * Which character the form is declaring for.
 *
 * **Pure, and a function of all three inputs**, because both ways this can go
 * stale are silent. A choice that outlives its **round** means a patron who
 * declared for their retainer in round 1 declares for them again in round 2
 * while their own character is never declared for and the window hangs on them
 * — and the picker's label is `sr-only`, so nothing on screen says so. A choice
 * that outlives the retainer's **presence in the fight** is worse: the picker
 * unmounts when they are knocked out, so every submit is refused server-side
 * with no control left to clear it.
 *
 * Empty string means "myself", which is what goes on the wire as *absent*.
 */
export function declaringFor(
  choice: { readonly round: number; readonly characterId: string } | null,
  round: number,
  offered: readonly { readonly characterId: string }[],
): string {
  if (!choice || choice.round !== round) return '';
  return offered.some((r) => r.characterId === choice.characterId) ? choice.characterId : '';
}

/**
 * The modes a held spell offers — empty for the overwhelming majority of
 * spells, which is what hides the picker.
 *
 * **Pure and exported for the same reason `selectedActionOf` is**: whether a
 * spell is currently *selected* is this form's own local state, invisible to
 * `renderToStaticMarkup` (there is no prop for "start with this spell
 * chosen"), so the only place the logic that decides the picker's visibility
 * can be asserted is here, directly — not by rendering and hoping the markup
 * shows it.
 */
/**
 * The animals typed into Animal Summoning's boxes — one box per attempt, trimmed, empty ones dropped. **One box
 * each, never one comma-separated line**: the bestiary names its animals "Bear, Brown" and "Rat, Giant", so a
 * comma cannot be the separator. Pure and exported so it is tested directly.
 */
export function animalsFromInputs(values: readonly string[]): string[] | undefined {
  const names = values.map((n) => n.trim()).filter((n) => n !== '');
  return names.length > 0 ? names : undefined;
}

export function spellModesFor(
  memorised: readonly {
    readonly name: string;
    readonly modes?: readonly { readonly id: string; readonly label: string }[];
  }[],
  heldSpell: string,
): readonly { readonly id: string; readonly label: string }[] {
  return memorised.find((m) => m.name === heldSpell)?.modes ?? [];
}

/**
 * The printed reverse's own name a held spell offers, if any — Bless's own
 * "Curse". `undefined` for the overwhelming majority of spells, which is
 * what hides the toggle. Pure and exported for the identical
 * `renderToStaticMarkup`-cannot-see-selection reason `spellModesFor` is.
 */
export function spellReverseNameFor(
  memorised: readonly { readonly name: string; readonly reverseName?: string }[],
  heldSpell: string,
): string | undefined {
  return memorised.find((m) => m.name === heldSpell)?.reverseName;
}

/**
 * `spellModesFor`'s own result, narrowed for a spell that ALSO offers a
 * reverse — Cure Blindness or Deafness's own `blind` mode belongs to the
 * reverse (Cause Blindness or Deafness) only; the forward cure takes no
 * mode at all. No current spell has modes AND a reverse without this
 * also being true, so withholding modes until `reversed` is chosen is
 * safe for Bless/Holy Word too (neither has modes, so this never changes
 * what they show). Pure and exported for the identical
 * `renderToStaticMarkup`-cannot-see-selection reason `spellModesFor` is —
 * this is the "modes present but hidden" bug a live drive caught, with no
 * control anywhere to ever check `reversed` for a spell nothing let you.
 */
export function effectiveSpellModesFor(
  memorised: readonly {
    readonly name: string;
    readonly modes?: readonly { readonly id: string; readonly label: string }[];
    readonly reverseName?: string;
  }[],
  heldSpell: string,
  reversed: boolean,
): readonly { readonly id: string; readonly label: string }[] {
  const hasReverse = spellReverseNameFor(memorised, heldSpell) !== undefined;
  if (hasReverse && !reversed) return [];
  return spellModesFor(memorised, heldSpell);
}

/**
 * The viewer's retainers who are actually standing in this fight.
 *
 * **One definition, used by the page for both the picker and the spell fetch.**
 * It lived inside the panel while the page ran `declaringFor` over the
 * *unfiltered* list, and the two disagreed: a choice left over from an earlier
 * fight had the page fetching a retainer's spell sheet while the panel, seeing
 * an empty picker, declared for the viewer's own character — so a spell chosen
 * off the retainer's list was submitted as the patron's.
 *
 * Conscious, not merely present: one who has been knocked out has no turn to
 * take, and offering them produces a declaration the server refuses.
 */
export function retainersPresent(
  retainers: readonly { readonly characterId: string; readonly name: string }[],
  combat: { readonly combatants: readonly { readonly id: string; readonly conscious: boolean }[] } | null,
): { readonly characterId: string; readonly name: string }[] {
  if (!combat) return [];
  return retainers.filter((r) =>
    combat.combatants.some((c) => c.id === `pc:${r.characterId}` && c.conscious),
  );
}

export interface CombatPanelProps {
  readonly combat: CombatView;
  readonly actions: readonly CombatActionDescriptor[];
  /** The viewer's own combatant, so the form knows who is declaring. */
  readonly characterId?: string | undefined;
  readonly onDeclare: (declaration: CombatDeclaration) => void;
  /**
   * What is in the room, from the DM's own description of the scene.
   *
   * ⚠ **Assembled on the server**, labels and all: which features exist and
   * what each is worth are rules, and a client-side copy would still render as
   * a confident label after the table changed. Same reason the verb bar reads
   * `GET /api/verbs`.
   */
  readonly sceneFeatures?: readonly SceneFeature[] | undefined;
  /**
   * The viewer's retainers **already narrowed to this fight**.
   *
   * Filtered by the caller, not here, and that is the fix for a real divergence:
   * this component filtered to the conscious combatants while the page ran the
   * same `declaringFor` over the unfiltered list, so a choice left over from an
   * earlier fight had the page fetching a retainer's spell sheet while the panel
   * declared for the viewer's own character. Two derivations of one answer.
   */
  readonly retainers?: readonly { readonly characterId: string; readonly name: string }[];
  /**
   * Who the form is declaring for — **resolved by the page**, through
   * `declaringFor`, and passed in.
   *
   * The page owns it because it is what fetches the acting character's slots: a
   * retainer's loadout is not the viewer's, and a picker deciding privately is
   * what left the cast form offering the wrong character's spells. Empty means
   * the viewer themselves.
   */
  readonly actingAs?: string;
  readonly onChoose?: (choice: { round: number; characterId: string } | null) => void;
  /**
   * The caster's unspent slots, for a `cast` declaration.
   *
   * Names only — the casting time it adds to initiative is read server-side
   * from the spell, so nothing here is a rule.
   */
  readonly memorised?: readonly {
    readonly name: string;
    readonly level: number;
    /**
     * The rare spell offering a real caster-chosen mode at cast time
     * (Monster Summoning VII). Absent for every ordinary spell.
     */
    readonly modes?: readonly { readonly id: string; readonly label: string }[];
    /** The printed reverse's own name — Bless's own "Curse". Absent for every spell that prints none. */
    readonly reverseName?: string;
    /** Whether this spell offers "optionally, a single item can be blessed". */
    readonly canBlessItem?: boolean;
    /** Animal Summoning: how many different kinds the caster may name. */
    readonly callsAnimals?: { readonly attempts: number };
  }[];
  /**
   * Where the acting character clicked on the shared encounter map, for a
   * `move` declaration. Owned by the page, not this panel: the click lands on
   * `MapPanel`, a sibling component, so the two can only agree through a
   * parent — the same reason `actingAs` is resolved there and passed in.
   */
  readonly pendingDestination?: { readonly mapX: number; readonly mapY: number } | null;
  /**
   * Tells the page whether the currently-selected action needs a map click at
   * all, so it knows whether to arm `MapPanel`'s `onPickPoint`. Fired from an
   * effect rather than computed by the page itself, because whether `move` is
   * selected is this form's own local state — the page has no view into it
   * otherwise, the identical reason `onChoose` exists for `actingAs`.
   */
  readonly onNeedsDestinationChange?: (needsDestination: boolean) => void;
}

/**
 * Whether this action is one the book lets an attacker hold back on.
 *
 * **Only the two unarmed styles.** A brawler "can pull his attacks" and a
 * wrestler chooses whether to add Strength; there is no printed way to pull a
 * sword, so offering the choice on an armed attack would put a house rule on
 * the screen looking exactly like the book.
 *
 * Exported and pure for the usual reason — a checkbox that renders but never
 * reaches the wire looks identical to one that works.
 *
 * **The known gap**: an *unarmed* `attack` is redirected to a brawl on the
 * server, and this cannot see the character's loadout to know that. A player
 * who wants to pull declares `brawl` outright, which is in the published set.
 */
export function holdingBackApplies(action: string | undefined): boolean {
  return action === 'brawl' || action === 'wrestle';
}

/**
 * Which action the form is actually offering.
 *
 * Pure and exported because **the catalogue arrives after this panel mounts**:
 * `actions` comes from `GET /api/verbs` over HTTP and the fight arrives over
 * the socket, so a client that reloads mid-combat renders with an empty set —
 * and `useState` reads its default once and never again. The selection stayed
 * `''` for the rest of that fight and the declaration submitted an empty
 * action. The same shape as `useState(!map.worldMapUrl)`, and equally
 * invisible to `renderToStaticMarkup`: the bug and the fix produce identical
 * markup, so the only place it can be asserted is here.
 */
export function selectedActionOf(
  actions: readonly CombatActionDescriptor[],
  chosen: string,
): string {
  return actions.some((a) => a.action === chosen) ? chosen : (actions[0]?.action ?? '');
}

export function CombatPanel({
  combat,
  actions,
  characterId,
  onDeclare,
  sceneFeatures = [],
  memorised = [],
  retainers = [],
  actingAs = '',
  onChoose,
  pendingDestination = null,
  onNeedsDestinationChange,
}: CombatPanelProps) {
  // The first published action, not a literal: the closed set is the
  // server's, and a hardcoded default is a second copy of it that would be
  // selected-but-absent the moment the set changed.
  const [action, setAction] = useState(actions[0]?.action ?? '');
  const [targetId, setTargetId] = useState('');
  const [weapon, setWeapon] = useState('');
  // Short is the band that carries no modifier, so it is the safe default.
  const [range, setRange] = useState<'short' | 'medium' | 'long'>('short');
  // **Off by default, and that is the rule rather than a UI default.** Not
  // holding back is the book's baseline; a box that remembered its last state
  // would have a character pulling blows in a fight nobody chose it for.
  const [holdingBack, setHoldingBack] = useState(false);
  const [spell, setSpell] = useState('');
  // Which of the spell's own printed modes was picked — only meaningful for
  // the rare spell that offers one. Re-derived below the same way `heldSpell`
  // is, so switching spells (or actors) never leaves a stale mode selected.
  const [spellMode, setSpellMode] = useState('');
  // Animal Summoning's animals, one box per attempt, as the caster typed them.
  const [animalNames, setAnimalNames] = useState<string[]>([]);
  // Whether to cast the held spell's printed REVERSE — Bless's own "Curse".
  // Off by default: the forward cast is the book's own reading of a
  // spell's name.
  const [reversed, setReversed] = useState(false);
  // Bless's own "optionally, a single item can be blessed" — free text,
  // like `weapon`, since there is no inventory on the sheet yet. Empty
  // means no item this cast.
  const [blessedWeapon, setBlessedWeapon] = useState('');
  // Where this character is putting themselves. `''` is out in the open, which
  // is the honest default: nobody starts a round already behind something.
  const [feature, setFeature] = useState('');


  // **Derived, because the catalogue arrives after this mounts.** `actions`
  // comes from `GET /api/verbs` and the fight comes over the socket, so a
  // client that reloads mid-combat renders this panel with an *empty* set —
  // and `useState` reads its default once and never again, so the selection
  // stayed `''` for the rest of the fight and the declaration submitted an
  // empty action. The same shape as `useState(!map.worldMapUrl)`, and equally
  // invisible to `renderToStaticMarkup`: the markup is identical either way.
  // Derived rather than re-synced in an effect, exactly like `heldSpell`.
  const selectedAction = selectedActionOf(actions, action);
  // ⚠ **Only the choosable ones.** Ground — a ford, ice, fog — applies to
  // everyone in the fight whatever they declare, so offering it would invite a
  // player to choose to stand in water they are already standing in.
  const choosableFeatures = (sceneFeatures ?? []).filter((f) => f.choosable);
  const groundFeatures = (sceneFeatures ?? []).filter((f) => !f.choosable);
  const chosen = actions.find((a) => a.action === selectedAction);

  // The page arms `MapPanel`'s `onPickPoint` only while a `move` is actually
  // selected — a click on the shared map means something different for every
  // other action, and leaving it armed after the player switches away would
  // make an ordinary "look at the map" click quietly become a declaration.
  useEffect(() => {
    onNeedsDestinationChange?.(!!chosen?.needsDestination);
  }, [chosen?.needsDestination, onNeedsDestinationChange]);
  // **The spell is re-read against the acting character's slots.** `memorised`
  // varies per actor now, so a name chosen for yourself and left selected when
  // the actor changes renders as a blank select and still submits — a refusal
  // for a spell the retainer does not hold. Derived rather than reset, for the
  // same reason `declaringFor` is.
  const heldSpell = memorised.some((m) => m.name === spell) ? spell : '';
  const reverseName = spellReverseNameFor(memorised, heldSpell);
  // Re-derived rather than reset, the identical stale-choice reason
  // `heldSpell`/`heldSpellMode` already stand on: a spell with no reverse
  // must never silently submit a stale `reversed: true` from a different
  // spell picked a moment ago.
  const wantsReversed = reverseName !== undefined && reversed;
  const spellModes = effectiveSpellModesFor(memorised, heldSpell, reversed);
  // **Re-derived exactly like `heldSpell`, for the identical stale-choice
  // reason.** A mode picked for one spell must not silently ride along when
  // the player switches to a different spell (or the acting character
  // changes underneath this form) — it would submit a mode id the newly
  // selected spell has never heard of.
  const heldSpellMode = spellModes.some((m) => m.id === spellMode) ? spellMode : '';
  const canBlessItem = memorised.find((m) => m.name === heldSpell)?.canBlessItem === true;
  const callsAnimals = memorised.find((m) => m.name === heldSpell)?.callsAnimals;
  // Already narrowed to this fight by the caller — see `retainers` above.
  const here = retainers;
  const actor = actingAs || characterId;
  const mine = actor ? `pc:${actor}` : undefined;
  const alreadyDeclared = mine !== undefined && combat.declared.includes(mine);
  const waiting = waitingOn(combat);
  const order = initiativeOrder(combat);

  return (
    <section className="space-y-3 rounded-md border border-amber-700/50 bg-amber-50/40 p-3 dark:bg-amber-950/20">
      <header className="flex flex-wrap items-baseline gap-2 text-sm">
        <span className="font-semibold uppercase tracking-wide">⚔ Combat — round {combat.round}</span>
        <span className="text-neutral-500 dark:text-neutral-400">
          {combat.phase === 'declaring'
            ? 'declaring'
            : combat.phase === 'resolving'
              ? 'resolving'
              : 'over'}
        </span>
        {combat.outcome && <span className="ml-auto">{combat.outcome}</span>}
      </header>

      <ul className="grid gap-1 text-sm sm:grid-cols-2">
        {combat.combatants.map((c) => (
          <li
            key={c.id}
            className={`flex items-baseline gap-2 ${c.conscious ? '' : 'line-through opacity-60'}`}
          >
            <span className={c.side === 'party' ? 'font-medium' : 'italic'}>{c.name}</span>
            <span className="text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
              {c.hitPoints}/{c.maxHitPoints}
            </span>
            {/* **What the table can see is on them** (Maz, 2026-10-20): perceptible effects on anyone, and the
                protections the party's own members know they hold. Labels come from the server, which
                alone knows which effect is which. */}
            {(c.effects ?? []).map((effect) => (
              <span
                key={effect}
                className="rounded-full border border-neutral-300 px-1.5 text-xs text-neutral-600 dark:border-neutral-700 dark:text-neutral-300"
              >
                {effect}
              </span>
            ))}
            {combat.declared.includes(c.id) && c.side === 'party' && (
              <span className="text-xs text-emerald-700 dark:text-emerald-400">
                {combat.summaries[c.id] ?? 'declared'}
              </span>
            )}
            {/* Theatre of the Mind is a first-class combat surface here, not
                a fallback: the engine tracks occupancy and nothing finer, so
                prose loses none of the state a grid would show. */}
            {zoneProse(combat, c.id) && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400">← {zoneProse(combat, c.id)}</span>
            )}
          </li>
        ))}
      </ul>

      {/* **The ground everyone is on**, reported rather than offered.
          Nobody chooses to be in a ford — it applies to every combatant
          whatever they declare — so a player who suddenly rolls badly needs
          somewhere on screen that says why. The effect is words and never a
          number: the figures are the book's and are applied server-side. */}
      {groundFeatures.length > 0 && (
        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          the whole fight is{' '}
          {groundFeatures.map((f) => f.effect || f.label).join(', ')}
        </p>
      )}

      {/* The declaration-status strip. Never a turn queue. */}
      {combat.phase === 'declaring' && (
        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          {waiting.length === 0
            ? 'everyone has declared'
            : `waiting on ${waiting.map((c) => c.name).join(', ')}`}
        </p>
      )}

      {order.length > 0 && (
        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          {/* Ascending, and it says so: a lower number acting first is the
              single most surprising thing about this ruleset's combat. */}
          initiative (low acts first):{' '}
          {order
            .map(({ id, total }) => `${combat.combatants.find((c) => c.id === id)?.name ?? id} ${total}`)
            .join(' · ')}
        </p>
      )}

      {combat.phase === 'declaring' && mine && (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            onDeclare({
              action: selectedAction,
              targetId: targetId || undefined,
              weapon: weapon || undefined,
              spell: heldSpell || undefined,
              // Only when the held spell actually offers one — a spell with
              // no modes never puts this on the wire at all.
              spellMode: heldSpellMode || undefined,
              // Only when the held spell actually offers a reverse — same
              // "never on the wire otherwise" reasoning as `spellMode`.
              reversed: wantsReversed || undefined,
              blessedWeapon: canBlessItem ? blessedWeapon || undefined : undefined,
              spellAnimals: callsAnimals ? animalsFromInputs(animalNames) : undefined,
              // Absent for the viewer's own character: the server reads that
              // as "myself", and sending it would be a claim it has to check
              // for no reason.
              characterId: actor === characterId ? undefined : actor,
              range,
              holdingBack: holdingBackApplies(selectedAction) ? holdingBack : undefined,
              // ⚠ Only when the room offers something to use. Sent even as
              // `''` — stepping out into the open is a decision — but never
              // sent at all when there is no picker, or every declaration in a
              // bare room would say "I am nowhere in particular", which is a
              // different claim from not mentioning it.
              ...(choosableFeatures.length > 0 ? { feature } : {}),
              ...(chosen?.needsDestination && pendingDestination
                ? { destination: pendingDestination }
                : {}),
            });
          }}
        >
          {/* Only when there is a choice to make. A table with no retainers
              never sees it, and the form reads exactly as it did. */}
          {here.length > 0 && (
            <>
              <label className="sr-only" htmlFor="combat-actor">
                Declaring for
              </label>
              <select
                id="combat-actor"
                value={actingAs}
                onChange={(e) =>
                  onChoose?.(
                    e.target.value ? { round: combat.round, characterId: e.target.value } : null,
                  )
                }
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">yourself</option>
                {here.map((r) => (
                  <option key={r.characterId} value={r.characterId}>
                    {r.name}
                  </option>
                ))}
              </select>
            </>
          )}

          <label className="sr-only" htmlFor="combat-action">
            Action
          </label>
          <select
            id="combat-action"
            value={selectedAction}
            onChange={(e) => setAction(e.target.value)}
            className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          >
            {actions.map((a) => (
              <option key={a.action} value={a.action}>
                {/* An action the engine does not carry out says so here,
                    rather than resolving into silence. */}
                {verbLabel(a.action)}
                {a.resolvedByEngine || a.action === 'pass' ? '' : ' (the DM narrates this)'}
              </option>
            ))}
          </select>

          {chosen?.needsTarget && (
            <>
              <label className="sr-only" htmlFor="combat-target">
                Target
              </label>
              <select
                id="combat-target"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">choose a target…</option>
                {combat.combatants
                  .filter((c) => c.conscious && c.id !== mine)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </>
          )}

          {/* **A move targets a point, not a creature** — there is no select
              to render, only a status. The click itself lands on `MapPanel`,
              a sibling component armed by `onNeedsDestinationChange` above;
              this just reports what has (or has not) been picked yet, the
              same "reported rather than offered" footing the ground features
              take below. The server refuses a `move` with no destination and
              a fight with no scaled map, so an unset click here fails loudly
              there rather than silently here. */}
          {chosen?.needsDestination && (
            <span className="text-xs text-neutral-600 dark:text-neutral-400">
              {pendingDestination
                ? `moving to (${Math.round(pendingDestination.mapX)}%, ${Math.round(pendingDestination.mapY)}%) — click the map again to change it`
                : 'click your destination on the encounter map'}
            </span>
          )}

          {/* A cast names one of the slots the character is actually holding.
              An empty list is the honest answer for a caster who has spent
              everything — there is no "cast from level" pool to fall back on. */}
          {/* `memorised` is **the acting character's**, fetched by the page for
              whoever this form is declaring for. Offering the viewer's own list
              while declaring for a retainer named spells they do not hold, and
              on a name collision would have spent *their* slot at a different
              level. */}
          {chosen?.addsCastingTime && (
            <>
              <label className="sr-only" htmlFor="combat-spell">
                Spell
              </label>
              <select
                id="combat-spell"
                value={heldSpell}
                onChange={(e) => setSpell(e.target.value)}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">
                  {memorised.length === 0 ? 'nothing memorised' : 'choose a spell…'}
                </option>
                {memorised.map((s, index) => (
                  <option key={`${s.name}-${index}`} value={s.name}>
                    {s.name} (L{s.level})
                  </option>
                ))}
              </select>
              {/* The number itself comes back on the ack, because it is read
                  from the spell — but the *direction* is the surprising part
                  and is worth saying before the round closes. */}
              <span className="text-xs text-neutral-500 dark:text-neutral-400">casting time delays you</span>
            </>
          )}

          {/* **Only the rare spell that offers one** (Monster Summoning
              VII's own "several weaker allies now, or one stronger ally two
              rounds from now"). A real tactical choice, never a coin flip —
              the server refuses the whole declaration if this is left blank
              for a spell that has modes, so there is no silent default. */}
          {spellModes.length > 0 && (
            <>
              <label className="sr-only" htmlFor="combat-spell-mode">
                Mode
              </label>
              <select
                id="combat-spell-mode"
                value={heldSpellMode}
                onChange={(e) => setSpellMode(e.target.value)}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">choose how…</option>
                {spellModes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </>
          )}

          {/* **Only when the held spell prints a reverse.** Bless's own
              "Curse" — a checkbox rather than a second select, since it is
              always exactly one alternative reading of the same spell name,
              never a choice among several. */}
          {reverseName !== undefined && (
            <label className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={reversed}
                onChange={(e) => setReversed(e.target.checked)}
              />
              Cast as {reverseName}
            </label>
          )}

          {/* **Only for a spell that calls animals** (Animal Summoning I–III). One free-text box per attempt (the
              book allows three, for three different animals); the server checks each against the bestiary and
              the spell's limits and refuses the declaration by name if one cannot be called. */}
          {callsAnimals &&
            Array.from({ length: callsAnimals.attempts }, (_, i) => (
              <span key={i} className="contents">
                <label className="sr-only" htmlFor={`combat-spell-animal-${i + 1}`}>
                  Animal {i + 1}
                </label>
                <input
                  id={`combat-spell-animal-${i + 1}`}
                  value={animalNames[i] ?? ''}
                  onChange={(e) =>
                    setAnimalNames((names) => {
                      const next = [...names];
                      next[i] = e.target.value;
                      return next;
                    })
                  }
                  placeholder={i === 0 ? 'animal to call, e.g. Wolf' : `another kind (${i + 1} of ${callsAnimals.attempts})`}
                  className="w-44 rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                />
              </span>
            ))}

          {/* **Only when the held spell offers item-blessing.** Free text,
              like `weapon` above — there is no inventory on the sheet yet,
              and the server refuses a name it cannot find in the pack.
              Always the caster's own weapon in this build; blessing an
              ally's own gear is not offered. */}
          {canBlessItem && (
            <>
              <label className="sr-only" htmlFor="combat-blessed-weapon">
                Item to bless
              </label>
              <input
                id="combat-blessed-weapon"
                value={blessedWeapon}
                onChange={(e) => setBlessedWeapon(e.target.value)}
                placeholder="bless an item, e.g. swordLong (optional)"
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              />
            </>
          )}

          {/* **The band, when the action can be at a distance.** The penalties
              are the table's (-2 medium, -5 long) and reached no attack at all
              until 2026-08-14 — the engine has no coordinates, so the shooter
              says how far and the server prices it. Shown for the actions that
              can be thrown or shot rather than on every declaration, because a
              sword swing has no range band. */}
          {(chosen?.action === 'attack' || chosen?.action === 'hurl') && (
            <>
              <label className="sr-only" htmlFor="combat-range">
                Range
              </label>
              <select
                id="combat-range"
                value={range}
                onChange={(e) => setRange(e.target.value as 'short' | 'medium' | 'long')}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="short">short range</option>
                <option value="medium">medium range</option>
                <option value="long">long range</option>
              </select>
            </>
          )}

          {/* **Where you put yourself**, offered only when the room has
              something to use. The DM names what is in the scene and this
              offers those things back — so a player cannot claim cover from a
              wall nobody described, and the server checks the same thing again
              because a picker is not a permission.

              Ground the whole fight is standing in (a ford, ice, fog) is
              deliberately **not** here: it applies to everyone whatever they
              declare, and offering it would invite somebody to choose to stand
              in water they are already in. It is reported below instead. */}
          {choosableFeatures.length > 0 && (
            <>
              <label className="sr-only" htmlFor="combat-feature">
                Position
              </label>
              <select
                id="combat-feature"
                value={feature}
                onChange={(e) => setFeature(e.target.value)}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                {/* "" is a real declaration — stepping out into the open — and
                    is why the wire sends the empty string rather than omitting
                    the field. */}
                <option value="">in the open</option>
                {choosableFeatures.map((f) => (
                  <option key={f.name} value={f.name}>
                    behind the {f.label}
                    {f.effect ? ` (${f.effect})` : ''}
                  </option>
                ))}
              </select>
            </>
          )}

          {/* **Holding back**, on the two unarmed styles the book gives the
              choice to. A brawler pulls his attacks and deals no lethal
              damage — the %KO still applies, so a pulled brawl still knocks
              people out; a wrestler withholds the Strength bonus. Neither
              could be said before this, so an unarmed fight could kill with
              no way to stop short of it. */}
          {holdingBackApplies(chosen?.action) && (
            <label className="flex items-center gap-1 text-xs text-neutral-600 dark:text-neutral-400">
              <input
                type="checkbox"
                checked={holdingBack}
                onChange={(e) => setHoldingBack(e.target.checked)}
                className="rounded border-neutral-300 dark:border-neutral-700"
              />
              hold back
            </label>
          )}

          {chosen?.needsWeapon && (
            <>
              <label className="sr-only" htmlFor="combat-weapon">
                Weapon
              </label>
              {/* Free text, because there is no inventory on the sheet yet and
                  a list of every weapon in the book would be a worse lie than
                  asking. The server refuses one it cannot find.
                  **Except for a flask**: Table 8.6 prints exactly four, none of
                  them a default, and the server refuses anything else — so the
                  placeholder names them rather than leaving a player to guess
                  at free text and have their declaration bounced. */}
              <input
                id="combat-weapon"
                value={weapon}
                onChange={(e) => setWeapon(e.target.value)}
                placeholder={
                  chosen.action === 'hurl'
                    ? 'Acid, Holy water, Greek fire, Poison'
                    : 'weapon, e.g. swordLong'
                }
                className="w-44 rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              />
            </>
          )}

          <button
            type="submit"
            className="rounded-md border border-neutral-300 px-3 py-1 text-sm font-medium dark:border-neutral-700"
          >
            {alreadyDeclared ? 'Change declaration' : 'Declare'}
          </button>

          {chosen?.initiative?.length ? (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {chosen.initiative
                .map((m) => `${m.name} ${m.value > 0 ? `+${m.value}` : m.value} initiative`)
                .join(', ')}
            </span>
          ) : null}
          {chosen?.initiativeOnTarget?.length ? (
            // Charging is the one action whose initiative effect lands on
            // someone else, and saying so is the difference between a player
            // understanding the trade and thinking the number is theirs.
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {chosen.initiativeOnTarget
                .map((m) => `target ${m.value > 0 ? `+${m.value}` : m.value} initiative`)
                .join(', ')}
            </span>
          ) : null}
        </form>
      )}
    </section>
  );
}
